import { useEffect, useState } from 'react';
import Icon from '../../a-atoms/icons/Icon';
import Button from '../buttons/Button';
import style from './Toaster.module.css';

/**
 * Toaster component for non-intrusive notifications
 * @param {boolean} show - Whether to show the toaster
 * @param {Function} onClose - Callback when toaster is closed
 * @param {string} position - Position of toaster: 'top' or 'bottom' (default: 'bottom')
 * @param {number} autoDismiss - Auto-dismiss after milliseconds (0 = no auto-dismiss)
 * @param {string} message - Message to display
 * @param {Array} actions - Array of action buttons: [{ name: string, onClick: Function }]
 * @param {boolean} showCloseButton - Whether to show close button (default: true)
 */
const Toaster = ({
  show,
  onClose,
  position = 'bottom',
  autoDismiss = 0,
  message,
  actions = [],
  showCloseButton = true,
  children
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (show) {
      // Small delay to trigger animation
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [show]);

  useEffect(() => {
    if (show && autoDismiss > 0) {
      const timer = setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, autoDismiss);
      return () => clearTimeout(timer);
    }
  }, [show, autoDismiss, onClose]);

  if (!show) {
    return null;
  }

  return (
    <div
      className={`${style.toaster} ${style[position]} ${isVisible ? style.visible : ''}`}
      role="alert"
      aria-live="polite"
    >
      <div className={style.content}>
        {message && <div className={style.message}>{message}</div>}
        {children && <div className={style.message}>{children}</div>}
        
        {actions.length > 0 && (
          <div className={style.actions}>
            {actions.map((action, index) => (
              <Button key={index} onClick={action.onClick} variants={action.variants}>
                {action.name}
              </Button>
            ))}
          </div>
        )}

        {showCloseButton && (
          <button
            className={style.closeButton}
            onClick={onClose}
            aria-label="Close notification"
          >
            <Icon>close</Icon>
          </button>
        )}
      </div>
    </div>
  );
};

export default Toaster;

